import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { UploadingLoader } from "../../utils/uploadingLoader";
import UploadIcon from "@mui/icons-material/Upload";
import VideocamIcon from "@mui/icons-material/Videocam";
import ConfirmationModal from "../../components/utils/ConfirmationModal";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { getCourseModules, saveModules } from "../../services/modulesServices";
import {
  AddModuleWrapper,
  ModuleContainer,
  StyledButton,
  InstructionBar,
} from "../../style/Admin/AddModule";
import DashboardHeader from "../../components/Common/DashboardHeader";
import { useNavigate } from "react-router-dom";

const AddModule = () => {
  const [courseData, setCourseData] = useState([]);
  const fileInputRef = useRef([]);
  const [status, setStatus] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showInstruction, setShowInstruction] = useState(true);
  const [loadingButton, setLoadingButton] = useState(null);
  const timerRef = useRef(null);
  const location = useLocation();
  const courseDetails = location.state?.data || {};
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const navigate = useNavigate();

  const allowedExtensions = [
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "mp4",
    "mpeg",
    "mov",
  ];

  useEffect(() => {
    startInstructionTimer();
    return () => clearTimeout(timerRef.current);
  }, []);

  const startInstructionTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowInstruction(false);
    }, 10000); // 10 seconds
  };

  const toggleInstruction = () => {
    setShowInstruction(true);
    startInstructionTimer();
  };
  const fetchModules = async () => {
    try {
      if (!courseDetails?.id) {
        console.log("No course ID available");
        return;
      }

      const response = await getCourseModules(courseDetails.id);
      if (response.success) {
        setCourseData(response.data);
        setStatus(response.data.status !== "draft" ? true : false);
      } else {
        toast.error("Failed to fetch course modules.");
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error("An error occurred while fetching course modules.");
    }
  };

  useEffect(() => {
    fetchModules();
  }, [courseDetails.id, refreshTrigger]);
  const handleOpenConfirmModal = () => {
    if (!courseData?.modules.length) {
      toast.error("Please add at least one module.");
      return;
    }
  
    for (const [moduleIndex, module] of courseData?.modules.entries()) {
      if (!module?.moduleName.trim() || !module?.moduleDescription.trim()) {
        toast.error(`Module ${moduleIndex + 1} name and description cannot be empty.`);
        return;
      }
  
      if (!module?.subModules?.length) {
        toast.error(`Please add at least one sub-module in module ${moduleIndex + 1}`);
        return;
      }
  
      for (const [subModuleIndex, subModule] of module?.subModules.entries()) {
        if (
          !subModule?.subModuleName.trim() ||
          !subModule?.subModuleDescription.trim()
        ) {
          toast.error(`Sub Module ${moduleIndex + 1}.${subModuleIndex + 1} name and description cannot be empty.`);
          return;
        }
  
        if (!subModule.file && !subModule.fileUrl) {
          toast.error(`Please upload a file for Sub Module ${moduleIndex + 1}.${subModuleIndex + 1}`);
          return;
        }
      }
    }
        setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const handleConfirmFinalSave = () => {
    handleSaveModules("final");
    setConfirmModalOpen(false);
  };
  const handleAddModule = () => {
    if (!courseData) return;

    const newModule = {
      moduleName: "",
      moduleDescription: "",
      subModules: [],
    };

    setCourseData((prev) => {
      prev.modules.push(newModule);
      return { ...prev };
    });
  };

  const handleDeleteModule = (index) => {
    setCourseData((prev) => {
      prev.modules.splice(index, 1);
      return { ...prev };
    });
  };

  const updateModuleName = (index, newTitle) => {
    setCourseData((prev) => {
      prev.modules[index].moduleName = newTitle;
      return { ...prev };
    });
  };

  const updateModuleDescription = (index, newDescription) => {
    setCourseData((prev) => {
      prev.modules[index].moduleDescription = newDescription;
      return { ...prev };
    });
  };

  const handleAddSubModule = (index) => {
    if (!courseData) return;

    const newSubModule = {
      subModuleName: "",
      subModuleDescription: "",
      file: null,
    };
    setCourseData((prev) => {
      prev.modules[index].subModules.push(newSubModule);
      return { ...prev };
    });
  };

  const handleDeleteSubModule = (index, subModuleIndex) => {
    setCourseData((prev) => {
      prev.modules[index].subModules.splice(subModuleIndex, 1);
      return { ...prev };
    });
  };

  const updateSubModuleName = (index, subModuleIndex, newTitle) => {
    setCourseData((prev) => {
      prev.modules[index].subModules[subModuleIndex].subModuleName = newTitle;
      return { ...prev };
    });
  };

  const updateSubModuleDescription = (
    index,
    subModuleIndex,
    newDescription
  ) => {
    setCourseData((prev) => {
      prev.modules[index].subModules[subModuleIndex].subModuleDescription =
        newDescription;
      return { ...prev };
    });
  };

  const prepareFileUpload = (index, subModuleIndex) => {
    fileInputRef.current[index][subModuleIndex].click();
  };
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const handleFileUpload = (event, index, subModuleIndex) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileExt = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error("File type not allowed. ");
      return;
    }

    const fileSize = formatFileSize(file.size);
    toast.info(`You've uploaded a file of ${fileSize}.`);

    let fileType = "other";
    if (file.type.includes("pdf")) fileType = "pdf";
    else if (file.type.includes("image")) fileType = "image";
    else if (file.type.includes("video")) fileType = "video";

    setCourseData((prev) => {
      prev.modules[index].subModules[subModuleIndex].fileName = file.name;
      prev.modules[index].subModules[subModuleIndex].fileType = fileType;
      prev.modules[index].subModules[subModuleIndex].file = file;
      return { ...prev };
    });
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <PictureAsPdfIcon />;
      case "image":
        return <ImageIcon />;
      case "video":
        return <VideocamIcon />;
      default:
        return <DescriptionIcon />;
    }
  };
  const handleDeleteFile = (index, subModuleIndex) => {
    setCourseData((prev) => {
      prev.modules[index].subModules[subModuleIndex].fileName = null;
      prev.modules[index].subModules[subModuleIndex].fileType = null;
      prev.modules[index].subModules[subModuleIndex].file = null;

      return { ...prev };
    });
  };

  const handleSaveModules = async (buttonType) => {
    if (!courseData) {
      toast.error("No course data available.");
      return;
    }
  
    setLoadingButton(buttonType);
    setIsLoading(true);
  
    try {
      const formData = new FormData();
      const payload = courseData;
  
      payload.status = buttonType === "draft" ? "draft" : "pending exam";

      for (const [moduleIndex, module] of payload?.modules.entries()) {
        for (const [subModuleIndex, subModule] of module?.subModules.entries()) {
          const fileKey = `file_${moduleIndex}_${subModuleIndex}`;
          if (subModule.file) {
            formData.append(fileKey, subModule.file);
            subModule.fileKey = fileKey;
          }
          delete subModule.file;
        }
      }
      formData.append("courseData", JSON.stringify(payload));
      const response = await saveModules(formData);
      if (response.success) {
        toast.success("Modules saved successfully!");
        setRefreshTrigger((prev) => !prev);
      } else {
        toast.error("Failed to save modules!");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ?? "An error occurred while saving modules."
      );
    } finally {
      setIsLoading(false);
      setLoadingButton(null);
    }
  };

  return (
    <>
      <ToastContainer />
      <InstructionBar className="instruction-container">
        {showInstruction ? (
          <Box className="instruction-banner">
            <Typography variant="body2" className="font12">
              <strong>Note:</strong> If your files are large, please save as
              draft modules one by one instead of uploading all at once.
            </Typography>
          </Box>
        ) : (
          <Box
            className="info-icon"
            onClick={toggleInstruction}
            title="Show Instructions"
          >
            <InfoIcon />
          </Box>
        )}
      </InstructionBar>

      <AddModuleWrapper>
        <DashboardHeader title="Add Module" />
        <div className="header-section">
          <Typography variant="body1">Add Module</Typography>
          <StyledButton
            className="module-btn"
            onClick={handleAddModule}
            disabled={!courseData || status}
          >
            <AddIcon /> Module
          </StyledButton>
        </div>

        <>
          <Paper elevation={2} className="course-container">
            <Typography variant="body1" className="course-title">
              {courseData?.courseName || "Untitled Course"}
            </Typography>

            <ModuleContainer>
              {courseData.modules?.map((module, index) => (
                <Accordion className="module-accordion" key={index}>
                  <AccordionSummary
                    className="module-header"
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <Typography variant="body2" className="font14">
                      <span className="module-index">Module {index + 1}:</span>
                      {module.moduleName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="module-details">
                    <Box className="module-content">
                      <div className="flex-input">
                        <div>
                          <Typography
                            variant="subtitle2"
                            className="field-label font12"
                          >
                            Module Title:
                          </Typography>
                          <input
                            placeholder="Enter Module Title"
                            value={module.moduleName || ""}
                            onChange={(e) =>
                              updateModuleName(index, e.target.value)
                            }
                            className="module-input"
                            disabled={status}
                          />
                        </div>
                        <div>
                          <Typography
                            variant="subtitle2"
                            className="field-label font12"
                          >
                            Module Description:
                          </Typography>
                          <textarea
                            placeholder="Enter Module Description"
                            value={module.moduleDescription || ""}
                            onChange={(e) =>
                              updateModuleDescription(index, e.target.value)
                            }
                            className="module-input"
                            disabled={status}
                          />
                        </div>
                      </div>
                      <Box className="module-actions">
                        <StyledButton
                          onClick={() => handleAddSubModule(index)}
                          disabled={status}
                        >
                          <AddIcon />
                        </StyledButton>
                        <StyledButton
                          className="submodule-btn font12"
                          onClick={() => handleDeleteModule(index)}
                          disabled={status}
                        >
                          <CloseIcon /> Module
                        </StyledButton>
                      </Box>
                    </Box>

                    <Box className="submodule-section">
                      <Typography
                        variant="subtitle1"
                        className="section-title font14"
                      >
                        Sub-Modules:
                      </Typography>
                      <List className="submodule-list">
                        {module.subModules?.map((subModule, subModuleIndex) => (
                          <Accordion
                            className="submodule-accordion"
                            key={subModuleIndex}
                          >
                            <AccordionSummary
                              className="submodule-header"
                              expandIcon={<ExpandMoreIcon />}
                            >
                              <Typography className="font14">
                                <span className="field-label font12">
                                  Sub-Module {index + 1}.{subModuleIndex + 1}:{" "}
                                </span>
                                {subModule.subModuleName}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails className="submodule-details">
                              <Box className="submodule-content">
                                <div className="flex-input">
                                  <div>
                                    <Typography
                                      variant="body2"
                                      className="field-label font12"
                                    >
                                      Sub-Module Title:
                                    </Typography>
                                    <input
                                      placeholder="Enter Sub-Module Title"
                                      value={subModule.subModuleName || ""}
                                      onChange={(e) =>
                                        updateSubModuleName(
                                          index,
                                          subModuleIndex,
                                          e.target.value
                                        )
                                      }
                                      className="submodule-input"
                                      disabled={status}
                                    />
                                  </div>
                                  <div>
                                    <Typography
                                      variant="body2"
                                      className="field-label font12"
                                    >
                                      Sub-Module Description:
                                    </Typography>
                                    <textarea
                                      placeholder="Enter Sub-Module Description"
                                      value={
                                        subModule.subModuleDescription || ""
                                      }
                                      onChange={(e) =>
                                        updateSubModuleDescription(
                                          index,
                                          subModuleIndex,
                                          e.target.value
                                        )
                                      }
                                      className="submodule-input"
                                      disabled={status}
                                    />
                                  </div>
                                </div>
                                <div className="submodule-action">
                                  <StyledButton
                                    className="submodule-btn font12"
                                    onClick={() =>
                                      handleDeleteSubModule(
                                        index,
                                        subModuleIndex
                                      )
                                    }
                                    disabled={status}
                                  >
                                    <CloseIcon />
                                    Sub Module
                                  </StyledButton>
                                </div>
                              </Box>

                              <Box className="materials-section">
                                <Typography
                                  variant="subtitle2"
                                  className="section-title font14"
                                >
                                  Materials:
                                </Typography>

                                <input
                                  type="file"
                                  ref={(inp) => {
                                    if (fileInputRef.current[index]) {
                                      fileInputRef.current[index][
                                        subModuleIndex
                                      ] = inp;
                                    } else {
                                      fileInputRef.current[index] = [];
                                      fileInputRef.current[index][
                                        subModuleIndex
                                      ] = inp;
                                    }
                                  }}
                                  style={{ display: "none" }}
                                  onChange={(e) =>
                                    handleFileUpload(e, index, subModuleIndex)
                                  }
                                  accept=".pdf,.jpg,.jpeg,.png,.gif,.mp4,.mpeg,.mov"
                                  disabled={status}
                                />
                                {subModule.fileName ? (
                                  <List dense className="file-list">
                                    <ListItem className="file-item">
                                      <ListItemIcon>
                                        {getFileIcon(subModule.fileType)}
                                      </ListItemIcon>
                                      <ListItemText
                                        className="font12"
                                        primary={subModule.fileName}
                                      />
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          disabled={status}
                                          edge="end"
                                          onClick={() =>
                                            handleDeleteFile(
                                              index,
                                              subModuleIndex
                                            )
                                          }
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  </List>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    className="empty-message font12"
                                  >
                                    No materials uploaded yet.
                                  </Typography>
                                )}
                              </Box>

                              {!subModule.fileName && (
                                <div className="file-upload">
                                  <StyledButton
                                    disabled={status}
                                    className="upload-btn font12"
                                    onClick={() =>
                                      prepareFileUpload(index, subModuleIndex)
                                    }
                                  >
                                    <UploadIcon /> Upload File
                                  </StyledButton>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    className="empty-message font12"
                                  >
                                    To ensure document security, we only accept PDF, Image, and Video files. Editable formats like Word and PPT are restricted to avoid potential tampering or unauthorized modifications.
                                  </Typography>
                                </div>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </List>
                      {!module.subModules?.length && (
                        <Typography
                          variant="body2"
                          className="empty-message font12"
                        >
                          No sub-modules yet. Add one below.
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
              {!courseData?.modules?.length && (
                <Typography
                  variant="body1"
                  className="empty-module-message font12"
                >
                  No modules yet. Click the "Module" button above to add one.
                </Typography>
              )}
            </ModuleContainer>
          </Paper>

          <div className="save-container">
            <StyledButton
              className="save-btn"
              onClick={() => handleSaveModules("draft")}
              disabled={status || isLoading}
            >
              {loadingButton === "draft" ? (
                <UploadingLoader />
              ) : (
                "Save As Draft"
              )}
            </StyledButton>

            <StyledButton
              className="save-btn"
              onClick={() => navigate(`/course/${courseDetails?.id}`)}
              disabled={status || isLoading}
            >
              Preview from user side
            </StyledButton>

            <StyledButton
              className="save-btn"
              onClick={handleOpenConfirmModal}
              disabled={status || isLoading}
            >
              {loadingButton === "final" ? <UploadingLoader /> : "Final Save"}
            </StyledButton>

            <ConfirmationModal
              open={confirmModalOpen}
              handleCancel={handleCloseConfirmModal}
              handleConfirm={handleConfirmFinalSave}
              isLoading={loadingButton === "final"}
            />
          </div>
        </>
      </AddModuleWrapper>
    </>
  );
};

export default AddModule;
